import { create } from "zustand";

import { TaskStatus } from "@/types/task";

import { useRightSidebarStore } from "./right-sidebar-store";
import {
  closeSiblingDrawers,
  registerSidebarDrawer,
} from "./sidebar-drawer-coordinator";
import { getTenantKey } from "./tenant-storage-helpers";
import { onTenantChange, useTenantStore } from "./tenant-store";
import { taskSidebarDefinition } from "@/features/sidebar/task-sidebar";

export type TaskPanelAssigneeFilter = "subscribed" | "assigned" | "all";
/** `null` means "use the user's saved preference" (matches the nav badge logic). */
export type TaskPanelAssigneeFilterState = TaskPanelAssigneeFilter | null;

/**
 * "ACTIVE" matches the sidebar badge count: OPEN + IN_PROGRESS tasks only.
 * "ALL" shows every non-archived status (OPEN, IN_PROGRESS, COMPLETED, CANCELLED).
 */
export type TaskPanelStatusFilter = TaskStatus | "ALL" | "ACTIVE";

export type TaskGroupBy = "priority" | "dueDate" | "assignee" | "status";

export interface TaskPanelFilters {
  /** Also picks the server sort, so each group arrives as one contiguous run. */
  groupBy: TaskGroupBy;
  filterStatus: TaskPanelStatusFilter;
  assigneeFilter: TaskPanelAssigneeFilterState;
}

export type TasksDrawerContext = { type: "call"; callId: string };

/**
 * Pinning is a deliberate user choice, so it outlives the drawer being closed
 * and the page being reloaded. Only transient state (open, context) resets.
 */
const PIN_STORAGE_KEY = "tasks-panel-locked";

/**
 * Both preferences are per-clinic. localStorage is shared across every tenant
 * on the origin, so an unscoped key would carry one clinic's pin, scope and
 * grouping into the next.
 *
 * `getCurrentTenant` falls back to the leading path segment, which is set well
 * before this module is imported in the browser.
 */
function tenantKey(baseName: string): string {
  return getTenantKey(baseName, useTenantStore.getState().getCurrentTenant());
}

function getPersistedLock(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(tenantKey(PIN_STORAGE_KEY)) === "true";
  } catch {
    // Private mode / blocked site data — fall back to unpinned.
    return false;
  }
}

function persistLock(isLocked: boolean): void {
  try {
    localStorage.setItem(tenantKey(PIN_STORAGE_KEY), String(isLocked));
  } catch {
    // Nothing to do — the lock still applies for this session.
  }
}

const DEFAULT_PANEL_FILTERS: TaskPanelFilters = {
  groupBy: "priority",
  filterStatus: "ACTIVE",
  assigneeFilter: null,
};

/**
 * How the panel is filtered is a deliberate choice, so it outlives a reload
 * the same way pinning does. Merged over the defaults on read, so a filter
 * added later gets its default rather than coming back undefined.
 */
const FILTERS_STORAGE_KEY = "tasks-panel-filters";

function getPersistedFilters(): TaskPanelFilters {
  if (typeof window === "undefined") return DEFAULT_PANEL_FILTERS;
  try {
    const stored = localStorage.getItem(tenantKey(FILTERS_STORAGE_KEY));
    if (!stored) return DEFAULT_PANEL_FILTERS;
    return { ...DEFAULT_PANEL_FILTERS, ...JSON.parse(stored) };
  } catch {
    // Unparseable or blocked storage — fall back to the defaults.
    return DEFAULT_PANEL_FILTERS;
  }
}

function persistFilters(filters: TaskPanelFilters): void {
  try {
    localStorage.setItem(
      tenantKey(FILTERS_STORAGE_KEY),
      JSON.stringify(filters),
    );
  } catch {
    // Nothing to do — the choice still holds for this session.
  }
}

interface TasksDrawerState {
  isOpen: boolean;
  isLocked: boolean;
  openDrawer: () => void;
  openCallLinkedTasks: (callId: string, taskId?: string) => void;
  closeDrawer: () => void;
  lockDrawer: () => void;
  unlockDrawer: () => void;
  context: TasksDrawerContext | null;
  clearContext: () => void;
  panelFilters: TaskPanelFilters;
  setPanelFilters: (
    update:
      | Partial<TaskPanelFilters>
      | ((prev: TaskPanelFilters) => Partial<TaskPanelFilters>),
  ) => void;
}

export const useTasksDrawerStore = create<TasksDrawerState>((set) => ({
  isOpen: false,
  isLocked: getPersistedLock(),
  openDrawer: () => {
    closeSiblingDrawers("tasks");
    set({ isOpen: true, context: null });
  },
  openCallLinkedTasks: (callId, taskId) => {
    closeSiblingDrawers("tasks");
    set({
      isOpen: true,
      context: { type: "call", callId },
    });
    if (taskId) {
      useRightSidebarStore.getState().open(taskSidebarDefinition, { taskId });
    }
  },
  closeDrawer: () =>
    set({
      isOpen: false,
      context: null,
    }),
  lockDrawer: () => {
    persistLock(true);
    set({ isLocked: true });
  },
  unlockDrawer: () => {
    persistLock(false);
    set({ isLocked: false });
  },
  context: null,
  clearContext: () => set({ context: null }),
  panelFilters: getPersistedFilters(),
  setPanelFilters: (update) =>
    set((state) => {
      const panelFilters = {
        ...state.panelFilters,
        ...(typeof update === "function" ? update(state.panelFilters) : update),
      };
      persistFilters(panelFilters);
      return { panelFilters };
    }),
}));

registerSidebarDrawer("tasks", () =>
  useTasksDrawerStore.getState().closeDrawer(),
);

// The store is a module singleton, so it reads storage once. Switching clinics
// is a client-side route change, not a reload — without this the panel keeps
// the previous clinic's scope, grouping and pin, and the next edit writes them
// under the new clinic's key.
onTenantChange(() => {
  useTasksDrawerStore.setState({
    isLocked: getPersistedLock(),
    panelFilters: getPersistedFilters(),
  });
});
